import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Search, Calendar, Download } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface Filing {
  id: number;
  filing_type: string;
  title: string;
  description: string;
  due_date: string;
  status: string;
  responsible_person: string;
  filed_date: string;
  filing_reference: string;
  evidence_url: string;
  notes: string;
  recurring: boolean;
  frequency: string;
  created_at: string;
}

interface DashboardStats {
  total: number;
  upcoming: number;
  overdue: number;
  filed_this_year: number;
  due_this_month: number;
}

const statusColors: Record<string, string> = {
  upcoming: 'bg-blue-100 text-blue-800',
  due: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  filed: 'bg-green-100 text-green-800',
  not_applicable: 'bg-gray-100 text-gray-600',
};

const FILING_TYPES = [
  'annual_accounts',
  'confirmation_statement',
  'vat_return',
  'corporation_tax',
  'paye_rtl',
  'annual_return',
  'ico_registration',
  'hmrc_notification',
  'pension_declaration',
  'gender_pay_gap',
  'modern_slavery_statement',
  'other',
];

const EMPTY_FORM = {
  filing_type: 'annual_accounts',
  title: '',
  description: '',
  due_date: '',
  responsible_person: '',
  recurring: false,
  frequency: 'annually',
};

const EMPTY_FILE_FORM = {
  filed_date: '',
  filing_reference: '',
  evidence_url: '',
  notes: '',
};

const StatutoryFilings: React.FC = () => {
  const [filings, setFilings] = useState<Filing[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState<'table' | 'timeline'>('table');
  const [createModal, setCreateModal] = useState(false);
  const [fileModal, setFileModal] = useState<Filing | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fileForm, setFileForm] = useState(EMPTY_FILE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);

      const [listRes, statsRes] = await Promise.all([
        fetch(`/api/compliance/statutory-filings?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/statutory-filings/dashboard', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setFilings(listRes.filings || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/statutory-filings', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      setCreateModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkFiled = async () => {
    if (!fileModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/statutory-filings/${fileModal.id}/file`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(fileForm),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update');
      setFileModal(null);
      setFileForm(EMPTY_FILE_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    setError('');
    try {
      const res = await fetch('/api/compliance/statutory-filings/seed-defaults', {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to seed');
      const data = await res.json();
      setSuccess(`Seeded ${data.count || 0} default UK filings.`);
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const getTimelineMonths = () => {
    const months: { label: string; filings: Filing[] }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      const monthFilings = filings.filter(f => {
        if (!f.due_date) return false;
        const fd = new Date(f.due_date);
        return fd.getMonth() === d.getMonth() && fd.getFullYear() === d.getFullYear();
      });
      if (monthFilings.length > 0) {
        months.push({ label, filings: monthFilings });
      }
    }
    return months;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Statutory Filings</h1>
        </div>
        <div className="flex gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <button onClick={() => setView('table')} className={`px-3 py-2 text-sm ${view === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Table</button>
            <button onClick={() => setView('timeline')} className={`px-3 py-2 text-sm ${view === 'timeline' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Timeline</button>
          </div>
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={handleSeedDefaults} disabled={seeding} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <Download size={16} /> {seeding ? 'Seeding...' : 'Seed UK Defaults'}
          </button>
          <button onClick={() => { setForm(EMPTY_FORM); setCreateModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Add Filing
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Filings', value: stats.total, color: 'text-gray-900' },
            { label: 'Upcoming', value: stats.upcoming, color: 'text-blue-600' },
            { label: 'Due This Month', value: stats.due_this_month, color: 'text-yellow-600' },
            { label: 'Overdue', value: stats.overdue, color: 'text-red-600' },
            { label: 'Filed This Year', value: stats.filed_this_year, color: 'text-green-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search filings..." value={search} onChange={e => setSearch(e.target.value)} className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Statuses</option>
          {['upcoming', 'due', 'overdue', 'filed', 'not_applicable'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : view === 'timeline' ? (
        <div className="space-y-6">
          {getTimelineMonths().length === 0 ? (
            <div className="bg-white border rounded-xl p-12 text-gray-500">No upcoming filings in the next 12 months.</div>
          ) : (
            getTimelineMonths().map(month => (
              <div key={month.label}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{month.label}</h3>
                <div className="space-y-2">
                  {month.filings.map(f => (
                    <div key={f.id} className="bg-white border rounded-xl p-4 flex items-center justify-between hover:border-blue-200 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="text-center w-12">
                          <p className="text-lg font-bold text-gray-900">{new Date(f.due_date).getDate()}</p>
                          <p className="text-xs text-gray-400">{new Date(f.due_date).toLocaleDateString('en-GB', { weekday: 'short' })}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{f.title}</p>
                          <p className="text-xs text-gray-500 capitalize">{f.filing_type.replace(/_/g, ' ')} {f.responsible_person ? `| ${f.responsible_person}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[f.status] || 'bg-gray-100 text-gray-700'}`}>
                          {f.status.replace(/_/g, ' ')}
                        </span>
                        {f.status !== 'filed' && (
                          <button onClick={() => { setFileModal(f); setFileForm(EMPTY_FILE_FORM); }} className="text-sm text-blue-600 hover:text-blue-800">
                            Mark Filed
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden">
          {filings.length === 0 ? (
            <div className="py-12 text-gray-500">No statutory filings found. Click "Seed UK Defaults" to get started.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Filing Type', 'Title', 'Due Date', 'Status', 'Responsible', 'Filed Date', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filings.map(f => {
                  const overdue = f.due_date && new Date(f.due_date) < new Date() && f.status !== 'filed';
                  return (
                    <tr key={f.id} className={`hover:bg-gray-50 ${overdue ? 'bg-red-50/50' : ''}`}>
                      <td className="px-4 py-3 text-left capitalize text-gray-600">{f.filing_type.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-left font-medium text-gray-900">{f.title}</td>
                      <td className="px-4 py-3 text-left text-gray-600">
                        {f.due_date ? new Date(f.due_date).toLocaleDateString() : '—'}
                        {overdue && <span className="ml-1 text-xs text-red-600 font-medium">OVERDUE</span>}
                      </td>
                      <td className="px-4 py-3 text-left">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[f.status] || 'bg-gray-100 text-gray-700'}`}>
                          {f.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left text-gray-600">{f.responsible_person || '—'}</td>
                      <td className="px-4 py-3 text-left text-gray-600">{f.filed_date ? new Date(f.filed_date).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-left">
                        {f.status !== 'filed' && (
                          <button onClick={() => { setFileModal(f); setFileForm(EMPTY_FILE_FORM); }} className="text-sm text-blue-600 hover:text-blue-800">
                            Mark Filed
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Filing Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Statutory Filing</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filing Type</label>
                  <select value={form.filing_type} onChange={e => setForm(f => ({ ...f, filing_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {FILING_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Person</label>
                <input value={form.responsible_person} onChange={e => setForm(f => ({ ...f, responsible_person: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))} />
                  Recurring
                </label>
                {form.recurring && (
                  <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm">
                    {['monthly', 'quarterly', 'annually'].map(fr => <option key={fr} value={fr}>{fr}</option>)}
                  </select>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={submitting || !form.title.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Add Filing'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Filed Modal */}
      {fileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Mark as Filed</h2>
              <p className="text-sm text-gray-500">{fileModal.title}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filed Date *</label>
                <input type="date" value={fileForm.filed_date} onChange={e => setFileForm(f => ({ ...f, filed_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filing Reference</label>
                <input value={fileForm.filing_reference} onChange={e => setFileForm(f => ({ ...f, filing_reference: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Companies House ref" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evidence URL / File</label>
                <input value={fileForm.evidence_url} onChange={e => setFileForm(f => ({ ...f, evidence_url: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Link to filed document" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={fileForm.notes} onChange={e => setFileForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setFileModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleMarkFiled} disabled={submitting || !fileForm.filed_date} className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {submitting ? 'Saving...' : 'Confirm Filed'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatutoryFilings;
